import {
  ContentBlock,
  GuideContent,
  GuideContentWrapped,
  GuideTypes,
  Metadata,
} from "../models";
import { Entry } from "contentful";
import {
  FieldMappingContainer,
  FieldMappingContainerWrapper,
  UserContextValues,
} from "../types";
import { PartnerContent } from "../schemas";
import { getFactoryByType } from "./factoriesByType";


const getCriteriaScore = (criteria: FieldMappingContainerWrapper<GuideContent>['criteria'], criteriaWeight?: FieldMappingContainerWrapper<GuideContent>['criteriaWeight']): number => {
  if (!criteria) {
    return 0;
  }

  // give it a bonus if the old and new phone are both specified 
  const bonus = criteria.oldPhone && criteria.newPhone ? 1 : 0;

  const criteriaWeightSum = (typeof criteriaWeight !== 'undefined' && Object.keys(criteriaWeight)?.map(key => typeof criteria[key] !== 'undefined' ? criteriaWeight[key] : 0)
    .reduce((a, b) => a + b)) || 0;

  return Object.keys(criteria).length + bonus + criteriaWeightSum; 
};

export type ValidEntries =  { content: FieldMappingContainer<GuideContent, string>[], criteriaMatched: number; }[];
export const getValidEntriesByCriteria = (
  rawEntries: FieldMappingContainerWrapper<GuideContent>[],
  userContextValues: UserContextValues
): ValidEntries => {
  const validEntries: ValidEntries = [];
  rawEntries?.forEach(({ criteria, criteriaWeight, content }) => {
    if (
      !criteria ||
      Object.keys(criteria).every(
        key => userContextValues[key] === criteria[key],
      )
    ) {
      validEntries.push({ content, criteriaMatched: getCriteriaScore(criteria, criteriaWeight)});
    }
  });

  if (validEntries.length === 0) {
    return [];
  }

  return validEntries;
}

export type GuideContentWrappedWithFieldMapping = GuideContentWrapped & { fieldMapping?: FieldMappingContainer<GuideContent, string>['fieldMapping'] };

export const createGuideContent = (
  entries: Entry<ContentBlock & Metadata>[],
  guideType: GuideTypes,
  userContextValues?: UserContextValues,
): GuideContentWrappedWithFieldMapping[] => {
  if (!userContextValues?.partner)
    throw new Error("partner must be supplied to create step");

  const partner = (userContextValues.partner as string).toLowerCase();
  if (!(partner in PartnerContent)) {
    throw new Error(`Guide mapping for ${partner} does not exist`);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const rawEntries = PartnerContent[partner][guideType] as FieldMappingContainerWrapper<GuideContent>[];
  const validEntries = getValidEntriesByCriteria(rawEntries, userContextValues);

  validEntries.sort((a, b) => a.criteriaMatched < b.criteriaMatched ? 1 : -1);
  const rawContent = validEntries[0]?.content;
  const processedContent: GuideContentWrappedWithFieldMapping[] = [];

  rawContent?.forEach(
    (content: FieldMappingContainer<GuideContent, string>) => {
      if (typeof getFactoryByType(content.type) === "undefined") {
        throw new Error(`Factory for type ${content.type} does not exist`);
      }

      const contentProcessed = getFactoryByType(content.type)(
        content.fieldMapping,
        entries,
        guideType,
        userContextValues,
      ) as GuideContent;
      if (typeof contentProcessed !== "undefined") {
        processedContent.push({
          type: content.type,
          content: contentProcessed,
          fieldMapping: content.fieldMapping
        });
      }
    },
  );

  return processedContent;
};
